import { DoctorRole, DoctorType } from '@prisma/client';

import { ADMIN_EMAILS } from '~/config/admins';
import { DENTIST_ROLES, OWNER_ROLES, getRoleDisplayLabel } from '~/config/roles';
import { DentalSpecialty } from '~/types/specialty';
import { isSpammyDoctorName } from '~/utils/name-validation';

/**
 * Formats a doctor role for display with proper titlecase
 * Uses the centralized role display mapping from config/roles.ts
 * Falls back to automatic formatting for unmapped roles
 *
 * @param role - The doctor role to format
 * @returns The formatted role string
 */
export function formatRoleForDisplay(role: string): string {
  // Try to get the standardized display label from config
  const displayLabel = getRoleDisplayLabel(role);
  if (displayLabel !== role) {
    return displayLabel;
  }

  // Fallback to automatic formatting for unmapped roles
  return role
    .replace(/_/g, ' ') // Replace underscores with spaces
    .split(' ') // Split into words
    .map(word => {
      // Keep abbreviations like RDH in uppercase
      if (['RDH', 'DMD', 'DDS'].includes(word.toUpperCase())) {
        return word.toUpperCase();
      }
      // Capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' '); // Join words back together
}

/**
 * Determines if a doctor role is considered a dentist role that provides direct patient services.
 * Dentist roles need additional setup like insurance carriers and membership plans.
 *
 * @param role - The doctor role to check
 * @returns true if the role is a dentist role (Associate Dentist or Owner Dentist)
 */
export function isDentistRole(role: string): boolean {
  return DENTIST_ROLES.includes(role as DoctorRole);
}

/**
 * Legacy roles kept in the DoctorRole enum for backward compatibility.
 * Profiles still carrying them are real dentists (see the same treatment in
 * network-invitations.server.ts isDentistRoleForCopyTemplateSignature), so
 * they must keep their degree.
 */
const LEGACY_DENTIST_ROLES: DoctorRole[] = [
  DoctorRole.OWNER,
  DoctorRole.ASSOCIATE,
  DoctorRole.PARTNER,
];

/**
 * Like isDentistRole, but also counts the legacy dentist roles (OWNER,
 * ASSOCIATE, PARTNER) that existing profiles still carry. Use for role/type
 * coupling so legacy dentists keep dentist treatment (#1225).
 */
export function isDentistOrLegacyDentistRole(role: string): boolean {
  return isDentistRole(role) || LEGACY_DENTIST_ROLES.includes(role as DoctorRole);
}

/**
 * Returns the DoctorType a profile must carry for the given role (#1225).
 * Ancillary staff are always NON_DENTIST. Dentist roles (incl. legacy) keep
 * their current dentist type when they have one, else General Dentist.
 */
export function coupledTypeForRole(role: string, currentType?: string | null): DoctorType {
  if (!isDentistOrLegacyDentistRole(role)) return DoctorType.NON_DENTIST;
  return currentType && isDentistType(currentType)
    ? (currentType as DoctorType)
    : DoctorType.GENERAL_DENTIST;
}

/**
 * Derives a dentist DoctorType from a submitted specialty list: any specialty
 * other than General Dentistry implies a Dental Specialist, otherwise General
 * Dentist. Shared across the team-member update/bulk-update endpoints (#1225).
 */
export function inferTypeFromSpecialty(specialty: string[]): DoctorType {
  const hasNonGeneral = specialty.some(
    s => s && s.trim() !== '' && s !== DentalSpecialty.GENERAL_DENTISTRY
  );
  return hasNonGeneral ? DoctorType.DENTAL_SPECIALIST : DoctorType.GENERAL_DENTIST;
}

/**
 * Returns the degree to persist for a profile with the given role.
 * The onboarding form only collects a degree for dentist roles, so any degree
 * submitted alongside a non-dentist role is stale form state (e.g. entered
 * before switching the role) and must not be saved — otherwise staff show up
 * with a dentist suffix like "Henry Davis, DDS" (#1229).
 *
 * @param role - The doctor role being saved
 * @param degree - The submitted degree value
 * @returns The trimmed degree for dentist roles (null when blank), null for all other roles
 */
export function degreeForRole(role: string, degree: string | null | undefined): string | null {
  if (!isDentistOrLegacyDentistRole(role)) {
    return null;
  }
  return degree?.trim() || null;
}

/**
 * Validation failures raised by applyRoleTypeCoupling — routes map these to
 * HTTP 400 (bad input), never 500.
 */
export class RoleTypeValidationError extends Error {}

/**
 * Enforces the role/type/specialty invariant on an update-shaped mutation
 * (#1225): ancillary staff are NON_DENTIST with no specialty; an explicit
 * switch to GENERAL_DENTIST sheds the specialty list (create-flow parity);
 * specialty-only updates infer the type for dentist roles. Mutates
 * `updates`/`before` in place the way the update endpoints build them.
 */
export function applyRoleTypeCoupling(
  current: { role: string; type: string; specialty: string[] },
  updates: Record<string, unknown>,
  before: Record<string, unknown>
): void {
  const effectiveRole = ((updates.role as string | undefined) ?? current.role) as string;
  if (updates.role !== undefined || updates.type !== undefined) {
    if (!isDentistOrLegacyDentistRole(effectiveRole)) {
      // Ancillary staff never carry a dentist type or specialties: ignore any
      // client-supplied values and clear what differs.
      delete updates.type;
      delete before.type;
      if (current.type !== 'NON_DENTIST') {
        before.type = current.type;
        updates.type = DoctorType.NON_DENTIST;
      }
      delete updates.specialty;
      delete before.specialty;
      if (current.specialty.length > 0) {
        before.specialty = current.specialty;
        updates.specialty = [];
      }
    } else {
      if (updates.type === 'NON_DENTIST') {
        throw new RoleTypeValidationError('Invalid type for a dentist role');
      }
      if (
        updates.type === undefined &&
        current.type === 'NON_DENTIST' &&
        updates.specialty === undefined
      ) {
        // Promoting ancillary → dentist without an explicit type: default to
        // General Dentist rather than keeping a contradictory NON_DENTIST.
        // When the same request also updates specialty, skip the default and
        // let the inference below derive the type from the specialty.
        before.type = current.type;
        updates.type = DoctorType.GENERAL_DENTIST;
      }
      if (updates.type === 'GENERAL_DENTIST') {
        // Explicit switch to General Dentist sheds the specialist's specialty
        // list — mirrors the create flow, where specialty is persisted only
        // for DENTAL_SPECIALIST.
        delete updates.specialty;
        delete before.specialty;
        if (current.specialty.length > 0) {
          before.specialty = current.specialty;
          updates.specialty = [];
        }
      }
    }
  }
  if (
    updates.specialty !== undefined &&
    updates.type === undefined &&
    isDentistOrLegacyDentistRole(effectiveRole)
  ) {
    // Inline specialty edits keep their existing semantics for dentists.
    updates.type = inferTypeFromSpecialty(updates.specialty as string[]);
    before.type = current.type;
  }
}

/**
 * Check if a doctor is a dental specialist
 * @param type
 */
export function isDentalSpecialist(type: string): boolean {
  return type === DoctorType.DENTAL_SPECIALIST;
}

/**
 * Determines if a doctor role is considered a non-dentist staff role.
 * Non-dentist roles typically don't provide direct patient services.
 *
 * @param role - The doctor role to check
 * @returns true if the role is a non-dentist staff role
 */
export function isStaffRole(role: string): boolean {
  return !isDentistRole(role);
}

/**
 * Determines if doctor role is an Owner role
 * Owner roles allow users to manage entities: offices, company, etc.
 * @param role
 */
export function isOwner(role: string): boolean {
  return OWNER_ROLES.includes(role as DoctorRole);
}

/**
 * Determines if a doctor role allows managing offices (create, edit).
 * Includes Owners, Associate Dentists, and Office Managers.
 * @param role
 */
export function canManageOffices(role: string): boolean {
  return (
    isOwner(role) || role === DoctorRole.ASSOCIATE_DENTIST || role === DoctorRole.OFFICE_MANAGER
  );
}

/**
 * Embed wizard settings (scope, routing, experience) can be edited by any role in the
 * company — matches validateEmbedManagement on the server. Company-membership and
 * main-office checks still apply there; this is no longer a role gate.
 */
export function canManageEmbedSettings(_role: string): boolean {
  return true;
}

/**
 * Determines if a doctor type is a dentist type that should be included in search results.
 * Only GENERAL_DENTIST and DENTAL_SPECIALIST types are considered valid for doctor searches.
 *
 * @param type - The doctor type to check
 * @returns true if the type is a dentist type that should appear in search results
 */
export function isDentistType(type: string): boolean {
  return type === DoctorType.GENERAL_DENTIST || type === DoctorType.DENTAL_SPECIALIST;
}

/**
 * Determines if a doctor profile should be excluded from search results.
 * Excludes the current user, system user, platform admin accounts, non-dentist types, staff roles,
 * and spammy names.
 *
 * @param doctor - The doctor profile to check
 * @param currentUserId - The ID of the current authenticated user (optional)
 * @returns true if the doctor should be excluded from search results
 */
export function shouldExcludeFromSearch(
  doctor: {
    id: string;
    email: string;
    type: string;
    role: string;
    firstName?: string;
    lastName?: string;
  },
  currentUserId?: string
): boolean {
  // Filter out the current authenticated user
  if (currentUserId && doctor.id === currentUserId) {
    return true;
  }

  // Filter out system user
  if (isSystemUser(doctor)) {
    return true;
  }

  // Platform admin accounts — not practice-linked directory dentists
  if (isAdminUser(doctor)) {
    return true;
  }

  // Filter out company members that are not dentists by type
  if (!isDentistType(doctor.type)) {
    return true;
  }

  // Filter out staff roles - only show actual dentists in search results
  if (!isDentistRole(doctor.role)) {
    return true;
  }

  // Filter out doctors with spammy names
  if (doctor.firstName && doctor.lastName) {
    if (isSpammyDoctorName(doctor.firstName, doctor.lastName)) {
      return true;
    }
  }

  return false;
}

/**
 * Determines if a doctor profile is an admin user.
 *
 * @param doctor - The doctor profile to check
 * @returns true if the doctor is an admin user
 */
export function isAdminUser(doctor: { email: string }): boolean {
  return ADMIN_EMAILS.includes(doctor.email.trim().toLowerCase());
}

/**
 * Determines if a doctor profile is the system user.
 * Used for filtering in contexts where we want to keep the current user but exclude system user.
 *
 * @param doctor - The doctor profile to check
 * @returns true if the doctor is the system user
 */
export function isSystemUser(doctor: { email?: string | null }): boolean {
  return doctor.email === 'system@toothreferral.com';
}

/**
 * Doctor type for onboarding saves: delegates to inferTypeFromSpecialty so a
 * dentist whose only selected specialty is General Dentistry still lands as
 * GENERAL_DENTIST (matching the #1225 team-endpoint semantics), everyone else
 * with a real specialty is DENTAL_SPECIALIST, and non-dentist roles are
 * NON_DENTIST.
 */
export function deriveDoctorType(
  role: string,
  specialty: string[]
): 'GENERAL_DENTIST' | 'DENTAL_SPECIALIST' | 'NON_DENTIST' {
  if (!isDentistRole(role)) return 'NON_DENTIST';
  return `${inferTypeFromSpecialty(specialty)}` as 'GENERAL_DENTIST' | 'DENTAL_SPECIALIST';
}
